import { trpc } from "@/lib/trpc";
import { checkTradingHours, smartInterval } from "@/lib/timeUtils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Briefcase } from "lucide-react";

export default function Positions() {
  const _isTH = checkTradingHours().isTradingHours;
  const { data: positions, isLoading } = trpc.positions.list.useQuery(undefined, { refetchInterval: smartInterval(30000, _isTH, 120000) });

  const totalValue = positions?.reduce((s, p) => s + p.currentPrice * p.quantity, 0) || 0;
  const totalPnl = positions?.reduce((s, p) => s + p.profitLoss, 0) || 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold flex items-center gap-2">
          <Briefcase className="h-5 w-5 text-primary" />
          持仓管理
        </h1>
        <p className="text-sm text-muted-foreground mt-1">实时查看当前持仓状态和盈亏情况</p>
      </div>

      {/* 汇总 */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-card border-border/50">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">持仓数量</p>
            <p className="text-xl font-bold">{positions?.length || 0} 只</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/50">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">持仓市值</p>
            <p className="text-xl font-bold">{totalValue.toFixed(0)} 元</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/50">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">总盈亏</p>
            <p className={`text-xl font-bold ${totalPnl >= 0 ? "text-gain" : "text-loss"}`}>
              {totalPnl >= 0 ? "+" : ""}{totalPnl.toFixed(0)} 元
            </p>
          </CardContent>
        </Card>
      </div>

      {/* 持仓列表 */}
      <Card className="bg-card border-border/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">持仓明细</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {positions && positions.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="border-border/50">
                  <TableHead className="text-xs">代码</TableHead>
                  <TableHead className="text-xs">名称</TableHead>
                  <TableHead className="text-xs text-right">持仓量</TableHead>
                  <TableHead className="text-xs text-right">成本价</TableHead>
                  <TableHead className="text-xs text-right">现价</TableHead>
                  <TableHead className="text-xs text-right">市值</TableHead>
                  <TableHead className="text-xs text-right">盈亏</TableHead>
                  <TableHead className="text-xs text-right">盈亏比</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {positions.map((p) => (
                  <TableRow key={p.id} className="border-border/30">
                    <TableCell className="text-xs font-mono">{p.stockCode}</TableCell>
                    <TableCell className="text-xs font-medium">{p.stockName}</TableCell>
                    <TableCell className="text-xs text-right">{p.quantity}</TableCell>
                    <TableCell className="text-xs text-right font-mono">{p.avgCost.toFixed(2)}</TableCell>
                    <TableCell className="text-xs text-right font-mono">{p.currentPrice.toFixed(2)}</TableCell>
                    <TableCell className="text-xs text-right">{(p.currentPrice * p.quantity).toFixed(0)}</TableCell>
                    <TableCell className={`text-xs text-right font-medium ${p.profitLoss >= 0 ? "text-gain" : "text-loss"}`}>
                      {p.profitLoss >= 0 ? "+" : ""}{p.profitLoss.toFixed(0)}
                    </TableCell>
                    <TableCell className={`text-xs text-right ${p.profitLossPct >= 0 ? "text-gain" : "text-loss"}`}>
                      {p.profitLossPct >= 0 ? "+" : ""}{p.profitLossPct.toFixed(2)}%
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <Briefcase className="h-10 w-10 mb-3 opacity-30" />
              <p className="text-sm">{isLoading ? "加载中..." : "暂无持仓"}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
